import * as React from 'react';
import classnames from 'classnames';
import { HcpGroupPresenceCounts, PRESENCE_COLORS, PresenceState } from '@ixicare/resources';
import { t } from '~i18n';
import styles from './HcpGroupStatusAggregate.module.css';

export interface HcpGroupAggregateProps {
  counts: HcpGroupPresenceCounts;
  /** Show cells even when count is 0. Off by default (hide zero cells). */
  showZeros?: boolean;
  className?: string;
}

const ORDER: PresenceState[] = [
  PresenceState.Online,
  PresenceState.Occupied,
  PresenceState.Unavailable,
  PresenceState.Offline,
];

const STATE_LABEL_KEYS: Record<PresenceState, string> = {
  [PresenceState.Online]: 'presence.state.online',
  [PresenceState.Occupied]: 'presence.state.occupied',
  [PresenceState.Unavailable]: 'presence.state.unavailable',
  [PresenceState.Offline]: 'presence.state.offline',
};

const HcpGroupStatusAggregate: React.FC<HcpGroupAggregateProps> = ({ counts, showZeros = false, className }) => {
  const cells = ORDER.map((state) => ({ state, count: counts[state] ?? 0 }));
  const visible = showZeros ? cells : cells.filter((c) => c.count > 0);
  if (visible.length === 0) return null;

  return (
    <div className={classnames(styles.Aggregate, className)} role="group" aria-label={t('profiles.table.status')}>
      {visible.map(({ state, count }) => {
        const isOffline = state === PresenceState.Offline;
        const label = t(STATE_LABEL_KEYS[state]);
        return (
          <span key={state} className={styles.Cell} title={label} aria-label={`${count} ${label}`}>
            <span
              className={classnames(styles.Dot, isOffline && styles.DotOffline)}
              style={isOffline ? undefined : { background: PRESENCE_COLORS[state] }}
              aria-hidden="true"
            />
            <span className={styles.Count}>{count}</span>
          </span>
        );
      })}
    </div>
  );
};

export default HcpGroupStatusAggregate;
